import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import {
  Box,
  Button,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material';
import { AsyncAutocomplete } from '@pro4-erp/core-web';
import { CodeList, getCodeLists } from '@/common';
import { FormType } from '../types';

type FormProps = {
  onSubmit: (data: Partial<FormType>) => void;
  initialState?: FormType;
  hasFormChanged?: (form: FormType) => boolean;
  onUnmount?: (form: FormType) => void;
  isFormEditable?: boolean;
};

const Form = ({ onSubmit, initialState, hasFormChanged, onUnmount, isFormEditable }: FormProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const form = useForm<FormType>({
    mode: 'onTouched',
    defaultValues: {
      visitUid: '',
      visitReportUid: '',
      status: null,
      reviewer: '',
      workTime: 0,
      note: '',
    },
  });

  useEffect(() => {
    return () => {
      if (onUnmount) {
        onUnmount(form.getValues());
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    form.reset({
      visitUid: initialState?.visitUid,
      clientName: initialState?.clientName,
      visitReportUid: initialState?.visitReportUid,
      status: initialState?.status,
      reviewer: initialState?.reviewer,
      workTime: initialState?.workTime,
      note: initialState?.note,
    });
  }, [initialState, form]);

  const baseSubmit = (data: FormType) => {
    if (hasFormChanged) {
      if (!hasFormChanged(data)) {
        enqueueSnackbar(t('Form was not changed.', { defaultValue: 'Form was not changed.' }), {
          variant: 'info',
        });
        return;
      }
    }
    if (data.status?.id === '71AF0B0A-CC2D-4379-833E-7E422C86A0E0' && !data.note) {
      enqueueSnackbar(t('Fill note', { defaultValue: 'Fill note.' }), {
        variant: 'error',
      });
      return;
    }
    onSubmit(data);
  };

  return (
    <Box component="form" onSubmit={form.handleSubmit(baseSubmit)} noValidate>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <List sx={{ display: 'flex', flexDirection: 'column' }}>
          <ListItem>
            <ListItemText
              primaryTypographyProps={{ fontSize: '1.25rem' }}
              secondaryTypographyProps={{ fontSize: '1rem' }}
              primary={t('Client', { defaultValue: 'Client' })}
              secondary={form.formState.defaultValues?.clientName}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primaryTypographyProps={{ fontSize: '1.25rem' }}
              secondaryTypographyProps={{ fontSize: '1rem' }}
              primary={t('Visit', { defaultValue: 'Visit' })}
              secondary={form.formState.defaultValues?.visitUid}
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primaryTypographyProps={{ fontSize: '1.25rem' }}
              secondaryTypographyProps={{ fontSize: '1rem' }}
              primary={t('Visit report', { defaultValue: 'Visit report' })}
              secondary={form.formState.defaultValues?.visitReportUid}
            />
          </ListItem>
        </List>
        <Controller
          control={form.control}
          name="note"
          render={({ field }) => (
            <TextField
              {...field}
              label={t('Explanations', { defaultValue: 'Explanations' })}
              fullWidth
              multiline
              disabled={!isFormEditable}
              error={!!form.formState.errors.note}
              helperText={form.formState.errors.note?.message?.toString()}
            />
          )}
        />

        <Controller
          control={form.control}
          name="workTime"
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ mt: '1rem' }}
              label={t('Work time', { defaultValue: 'Work time' })}
              fullWidth
              error={!!form.formState.errors.workTime}
              helperText={form.formState.errors.workTime?.message?.toString()}
              type="number"
              inputProps={{ step: 0.01 }}
            />
          )}
        />

        <AsyncAutocomplete<CodeList>
          id="status"
          label={t('Status', { defaultValue: 'Status' })}
          fullWidth
          disabled={!isFormEditable}
          getData={async (search) => {
            const newSearch = search === form.getValues().status?.name ? '' : search;
            const response = await getCodeLists({
              page: 1,
              pageSize: 5,
              search: newSearch,
              codeType: 'VisitRepReviewStatus',
            });

            return response.results;
          }}
          getOptionLabel={(option) => `${option.name}` || 'N/A'}
          isOptionEqualToValue={(option, value) => option.id === (!value ? undefined : value.id)}
          selectedOption={form.getValues().status}
          setSelectedOption={(option) => {
            form.clearErrors('status');

            if (option === null) {
              form.setError('status', {
                message: t('Status is required.', {
                  defaultValue: 'Status is required. ',
                }).toString(),
              });
            }

            form.setValue('status', option, { shouldValidate: true, shouldDirty: true });
          }}
        />
      </Box>

      <Button variant="contained" type="submit" fullWidth sx={{ mt: '1rem' }}>
        {t('Submit', { defaultValue: 'Submit' })}
      </Button>

      {!!form.formState.errors.root && (
        <FormHelperText error>{form.formState.errors.root.message}</FormHelperText>
      )}
    </Box>
  );
};

export default Form;
